/**
 * PWA Detection and Opening Utilities
 * Detects if app is running as PWA and provides methods to open URLs in PWA context
 */

/**
 * Detects if the app is running in standalone mode (PWA installed)
 */
export const isPWAInstalled = (): boolean => {
    // Check if running in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    // Check for iOS standalone
    const isIOSStandalone = (window.navigator as any).standalone === true;

    return isStandalone || isIOSStandalone;
};

/**
 * Detects if the device is Android
 */
export const isAndroid = (): boolean => {
    return /Android/i.test(navigator.userAgent);
};

/**
 * Detects if the device is iOS
 */
export const isIOS = (): boolean => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * Get custom URL scheme for each parliament app
 */
export const getAppScheme = (appName: string): string | undefined => {
    const schemes: { [key: string]: string } = {
        'coin': 'web+coinparliament',
        'sport': 'web+sportparliament',
        'stock': 'web+stockparliament',
        'voting': 'web+votingparliament',
        'fun': 'web+funparliament',
        'poker': 'web+pokerparliament',
        'anime': 'web+animevoting',
    };

    return schemes[appName];
};

/**
 * Get package name for each parliament app (for Android Intent URLs)
 * Note: These are placeholder package names - replace with actual ones
 */
export const getAppPackageName = (appName: string): string | undefined => {
    const packages: { [key: string]: string } = {
        'coin': 'com.coinparliament.twa',
        'sport': 'com.sportparliament.twa',
        'stock': 'com.stockparliament.twa',
        'voting': 'com.votingparliament.twa',
        'fun': 'com.funparliament.twa',
        'poker': 'com.pokerparliament.twa',
        'anime': 'com.animevoting.twa',
    };

    return packages[appName];
};

/**
 * Generates Android Intent URL for opening PWAs
 */
export const generateIntentUrl = (
    url: string,
    packageName: string,
    fallbackUrl: string
): string => {
    const intentUrl = `intent://${url.replace(/^https?:\/\//, '')}#Intent;` +
        `scheme=https;` +
        `package=${packageName};` +
        `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};` +
        `end`;

    return intentUrl;
};

/**
 * Attempts to open URL using custom scheme (for PWA deep linking)
 */
const tryCustomScheme = (url: string, scheme: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const schemeUrl = `${scheme}://${url.replace(/^https?:\/\//, '')}`;

        // Create a hidden iframe to trigger the custom scheme
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = schemeUrl;
        document.body.appendChild(iframe);

        // Set a timeout to check if it worked
        setTimeout(() => {
            document.body.removeChild(iframe);
            resolve(false); // Assume it didn't work if we're still here
        }, 1500);
    });
};

/**
 * Unified function to open URLs in PWA with fallback to browser
 * This is the main function you should use
 */
export const openInPWAOrBrowser = async (
    url: string,
    appName: string,
    options?: {
        telegram?: boolean;
        telegramInitData?: string;
    }
): Promise<void> => {
    // Prepare final URL
    let finalUrl = url;
    if (options?.telegram && options?.telegramInitData) {
        const separator = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${separator}inituserData=${encodeURIComponent(options.telegramInitData)}`;
    }

    console.log('Attempting to open:', finalUrl, 'for app:', appName);

    // Strategy 1: Try Android Intent URL (most reliable for Android)
    if (isAndroid()) {
        const packageName = getAppPackageName(appName);
        if (packageName) {
            console.log('Using Android Intent URL with package:', packageName);
            const intentUrl = generateIntentUrl(finalUrl, packageName, finalUrl);
            window.location.href = intentUrl;
            return;
        }
    }

    // Strategy 2: Try custom URL scheme (works for iOS and some Android)
    const customScheme = getAppScheme(appName);
    if (customScheme) {
        console.log('Trying custom scheme:', customScheme);
        const schemeWorked = await tryCustomScheme(finalUrl, customScheme);
        if (schemeWorked) {
            return;
        }
    }

    // Strategy 3: Fallback to normal browser opening
    console.log('Falling back to browser opening');
    window.open(finalUrl, '_blank');
};

/**
 * Simple version - just opens in new tab/window
 * Use this if the advanced PWA opening doesn't work
 */
export const openInBrowser = (
    url: string,
    options?: {
        telegram?: boolean;
        telegramInitData?: string;
    }
): void => {
    let finalUrl = url;
    if (options?.telegram && options?.telegramInitData) {
        const separator = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${separator}inituserData=${encodeURIComponent(options.telegramInitData)}`;
    }

    window.open(finalUrl, '_blank');
};
